import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAllProjects } from '@/shared/api/base-api';
import { Project } from '@/types/base';
import { useUser } from '@/shared/hooks/useUser';
import { BaseLayout } from '@/components/layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const AllProjects: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeRegion, setActiveRegion] = useState<'denver' | 'austin'>('denver');

  // Fetch all projects
  const { data: projectsData, isLoading, error } = useQuery<Project[]>({
    queryKey: ['all-projects'],
    queryFn: () => getAllProjects(),
  });

  // Ensure projects is always an array
  const projects = Array.isArray(projectsData) ? projectsData : [];

  // Filter projects by search query and region
  const filteredProjects = useMemo(() => {
    let filtered = projects;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (project) =>
          project.name.toLowerCase().includes(query) ||
          (project.address && project.address.toLowerCase().includes(query))
      );
    }

    // Apply region filter based on client region
    filtered = filtered.filter((project) => {
      // Check if any of the project's clients match the selected region
      const projectClients = Array.isArray(project.clients) ? project.clients : [];
      if (projectClients.length === 0) return true; // Show if no clients
      
      return projectClients.some((client) => {
        if (typeof client === 'object' && 'region' in client) {
          const region = client.region;
          return activeRegion === 'denver' ? region === 'DEN' : region === 'AUS';
        }
        return true; // Show if we can't determine region
      });
    });

    return filtered;
  }, [projects, searchQuery, activeRegion]);

  const handleProjectClick = (project: Project) => {
    // Navigate to the project's dashboard using the first client
    const firstClient = Array.isArray(project.clients) && project.clients.length > 0
      ? (typeof project.clients[0] === 'object' && 'name' in project.clients[0] 
          ? project.clients[0].name 
          : user?.client_info?.name)
      : user?.client_info?.name;
    
    if (firstClient) {
      navigate(`/app/dashboard/${firstClient}`);
    }
  };

  const getProjectThumbnail = (project: Project) => {
    return project.thumbnail_url || project.thumbnail || '/static/images/default_thumbnail.jpeg';
  };

  if (!user?.is_staff) {
    return (
      <BaseLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-muted-foreground">You don't have permission to view this page.</p>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground">All Projects</h1>
            <p className="mt-2 text-muted-foreground">Browse and manage all projects across regions</p>
          </div>

          {/* Search Bar */}
          <div className="mb-6 flex justify-center">
            <div className="w-full max-w-2xl">
              <Input
                type="text"
                placeholder="Search projects by name or address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>
          </div>

          {/* Region Toggle */}
          <div className="mb-8 flex justify-center">
            <div className="inline-flex h-10 items-center bg-muted border border-border rounded-lg p-1 gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveRegion('denver')}
                className={`rounded-md px-4 font-medium transition-colors ${
                  activeRegion === 'denver'
                    ? 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                Denver
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveRegion('austin')}
                className={`rounded-md px-4 font-medium transition-colors ${
                  activeRegion === 'austin'
                    ? 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                Austin
              </Button>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-12">
              <p className="text-destructive">Error loading projects. Please try again.</p>
            </div>
          )}

          {/* Projects Grid */}
          {!isLoading && !error && (
            <>
              {filteredProjects.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredProjects.map((project) => (
                    <div
                      key={project.id}
                      onClick={() => handleProjectClick(project)}
                      className="bg-card rounded-lg border border-border shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden group"
                    >
                      {/* Project Thumbnail */}
                      <div className="aspect-[3/2] overflow-hidden bg-muted">
                        <img
                          src={getProjectThumbnail(project)}
                          alt={project.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/static/images/default_thumbnail.jpeg';
                          }}
                        />
                      </div>

                      {/* Project Info */}
                      <div className="p-4">
                        <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                          {project.name}
                        </h3>
                        {project.address && (
                          <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                            {project.address}
                          </p>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                          {project.is_active ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-accent text-accent-foreground">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                              Completed
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg
                    className="mx-auto h-12 w-12 text-muted-foreground"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-foreground">No projects found</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {searchQuery
                      ? 'Try adjusting your search criteria'
                      : 'No projects available in this region'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </BaseLayout>
  );
};

export default AllProjects;

